import { ComponentRef } from '@angular/core';

import { ComponentPortal, Portal, PortalHost, TemplatePortal } from './portal';
import {
  NullPortalError,
  PortalAlreadyAttachedError,
  PortalHostAlreadyDisposedError,
  UnknownPortalTypeError,
} from './portal-errors';

/**
 * Partial implementation of PortalHost that only deals with attaching either a
 * ComponentPortal or a TemplatePortal.
 */
export abstract class BasePortalHost implements PortalHost {
  /** The portal currently attached to the host. */
  private _attachedPortal: Portal<any> | null;

  /** A function that will permanently dispose this host. */
  private _disposeFn: null | (() => void);

  /** Whether this host has already been permanently disposed. */
  private _isDisposed: boolean = false;

  /** Whether this host has an attached portal. */
  public hasAttached(): boolean {
    return !!this._attachedPortal;
  }

  public attach<T>(portal: Portal<T>): ComponentRef<T> | Map<string, any> {
    if (!portal) {
      throw new NullPortalError();
    }

    if (this.hasAttached()) {
      throw new PortalAlreadyAttachedError();
    }

    if (this._isDisposed) {
      throw new PortalHostAlreadyDisposedError();
    }

    if (portal instanceof ComponentPortal) {
      this._attachedPortal = portal;
      return this.attachComponentPortal(portal);
    } else if (portal instanceof TemplatePortal) {
      this._attachedPortal = portal;
      return this.attachTemplatePortal(portal);
    }

    throw new UnknownPortalTypeError();
  }

  public abstract attachComponentPortal<T>(
    portal: ComponentPortal<T>,
    index?: number,
  ): ComponentRef<T>;

  public abstract attachTemplatePortal(portal: TemplatePortal): Map<string, any>;

  public detach(): void {
    if (this._attachedPortal) {
      this._attachedPortal.setAttachedHost(null);
      this._attachedPortal = null;
    }

    this._invokeDisposeFn();
  }

  public dispose(): void {
    if (this.hasAttached()) {
      this.detach();
    }

    this._invokeDisposeFn();
    this._isDisposed = true;
  }

  public setDisposeFn(fn: () => void): void {
    this._disposeFn = fn;
  }

  private _invokeDisposeFn(): void {
    if (this._disposeFn) {
      this._disposeFn();
      this._disposeFn = null;
    }
  }
}
